'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSupabase } from '@/components/providers/supabase-provider';
import { submitFeedbackAction } from '@/app/actions/feedback';
import { extractDomainFromEmail, extractDomainFromUrl, checkDomainMatch } from '@/lib/feedback-utils';
import { toast } from 'sonner';
import {
  Bug,
  Lightbulb,
  ShieldAlert,
  Loader2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Lock,
} from 'lucide-react';
import { AuthModal } from '@/components/auth/AuthModal';
import { cn } from '@/lib/utils';

export type ProblemType = 'bug' | 'feature_request' | 'tool_removal' | 'other';

interface ReportProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolId?: string;
  toolName?: string;
  toolWebsiteUrl?: string;
  defaultType?: ProblemType;
}

export default function ReportProblemModal({
  isOpen,
  onClose,
  toolId,
  toolName,
  toolWebsiteUrl,
  defaultType = 'bug',
}: ReportProblemModalProps) {
  const { user, supabase } = useSupabase();

  const [type, setType] = useState<ProblemType>(defaultType);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // User Profile state for LinkedIn verification
  const [linkedinVerified, setLinkedinVerified] = useState<boolean | null>(null);
  const [userProfileEmail, setUserProfileEmail] = useState<string>('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPageUrl(window.location.href);
    }
  }, [isOpen]);

  useEffect(() => {
    if (defaultType) {
      setType(defaultType);
    }
  }, [defaultType]);

  // Fetch current user's profile to inspect linkedin_verified
  useEffect(() => {
    if (!user) {
      setLinkedinVerified(null);
      return;
    }

    const currentUser = user;
    async function loadUserProfile() {
      if (!currentUser) return;
      setIsLoadingProfile(true);
      try {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('linkedin_verified, email')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (prof) {
          setLinkedinVerified(prof.linkedin_verified === true);
          setUserProfileEmail(prof.email || currentUser.email || '');
        } else {
          setLinkedinVerified(false);
          setUserProfileEmail(currentUser.email || '');
        }
      } catch {
        setLinkedinVerified(false);
      } finally {
        setIsLoadingProfile(false);
      }
    }

    if (isOpen) {
      loadUserProfile();
    }
  }, [user, supabase, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!description.trim()) {
      toast.error('Please describe the problem or request.');
      return;
    }

    if (type === 'tool_removal' && !linkedinVerified) {
      toast.error('LinkedIn verification is required to submit a tool removal request.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await submitFeedbackAction({
        type,
        subject: subject.trim() || undefined,
        description: description.trim(),
        pageUrl,
        toolId: toolId || undefined,
        toolName: toolName || undefined,
        toolWebsiteUrl: toolWebsiteUrl || undefined,
      });

      if (res.success) {
        if (type === 'tool_removal') {
          if (res.requesterDomainMatched) {
            toast.success('Removal request submitted with verified work domain. Our team will process this promptly.');
          } else {
            toast.success('Removal request received. A team member will manually review your domain verification.');
          }
        } else {
          toast.success('Thank you! Your feedback has been received.');
        }

        setDescription('');
        setSubject('');
        onClose();
      } else {
        toast.error(res.error || 'Failed to submit request.');
      }
    } catch {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isToolContext = !!toolId || !!toolName;
  const toolDomain = toolWebsiteUrl ? extractDomainFromUrl(toolWebsiteUrl) : '';
  const userDomain = userProfileEmail ? extractDomainFromEmail(userProfileEmail) : '';
  const domainMatches = userDomain && toolDomain ? checkDomainMatch(userProfileEmail, toolWebsiteUrl || '') : false;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-lg bg-card border-border/80 p-6 sm:p-8 rounded-3xl shadow-2xl overflow-hidden">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-primary" />
              Report a Problem
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {isToolContext ? (
                <>Regarding <span className="font-bold text-foreground">{toolName || 'this tool'}</span></>
              ) : (
                'Help us improve GuideToReviews. Report broken links, suggest features, or submit feedback.'
              )}
            </DialogDescription>
          </DialogHeader>

          {!user ? (
            <div className="p-6 rounded-2xl bg-muted/40 border border-border/60 text-center space-y-4 my-2">
              <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <Lock className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">Authentication Required</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Please log in or create an account to submit problem reports, feature requests, or removal inquiries.
                </p>
              </div>
              <Button
                onClick={() => setIsAuthModalOpen(true)}
                className="rounded-xl px-6 font-bold text-xs h-10 shadow-md"
              >
                Log In to Continue
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 mt-3">
              {/* Type Selection */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Request Type
                </label>
                <div className="grid grid-cols-3 gap-2.5">
                  {/* Bug */}
                  <button
                    type="button"
                    onClick={() => setType('bug')}
                    className={cn(
                      'p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer select-none',
                      type === 'bug'
                        ? 'bg-amber-500/10 border-amber-500/50 text-amber-500 ring-1 ring-amber-500/30'
                        : 'bg-card/60 border-border/60 hover:bg-muted text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Bug className="h-4 w-4 mb-2" />
                    <span className="text-xs font-bold leading-tight">Bug</span>
                  </button>

                  {/* Feature Request */}
                  <button
                    type="button"
                    onClick={() => setType('feature_request')}
                    className={cn(
                      'p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer select-none',
                      type === 'feature_request'
                        ? 'bg-primary/10 border-primary/50 text-primary ring-1 ring-primary/30'
                        : 'bg-card/60 border-border/60 hover:bg-muted text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Lightbulb className="h-4 w-4 mb-2" />
                    <span className="text-xs font-bold leading-tight">Feature Request</span>
                  </button>

                  {/* Tool Removal Request (if tool context) OR Other (if general) */}
                  {isToolContext ? (
                    <button
                      type="button"
                      onClick={() => setType('tool_removal')}
                      className={cn(
                        'p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer select-none',
                        type === 'tool_removal'
                          ? 'bg-rose-500/10 border-rose-500/50 text-rose-500 ring-1 ring-rose-500/30'
                          : 'bg-card/60 border-border/60 hover:bg-muted text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <ShieldAlert className="h-4 w-4 mb-2" />
                      <span className="text-xs font-bold leading-tight">Removal Request</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setType('other')}
                      className={cn(
                        'p-3 rounded-xl border text-left flex flex-col justify-between transition-all cursor-pointer select-none',
                        type === 'other'
                          ? 'bg-blue-500/10 border-blue-500/50 text-blue-500 ring-1 ring-blue-500/30'
                          : 'bg-card/60 border-border/60 hover:bg-muted text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <ShieldAlert className="h-4 w-4 mb-2" />
                      <span className="text-xs font-bold leading-tight">Other / General</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Tool Removal Gating Warnings & Domain Match Preview */}
              {type === 'tool_removal' && (
                <div className="space-y-3 animate-in fade-in duration-300">
                  {isLoadingProfile ? (
                    <div className="p-4 rounded-xl bg-muted/40 border border-border/60 flex items-center gap-3 text-xs text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      Checking account verification status...
                    </div>
                  ) : !linkedinVerified ? (
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-500 dark:text-amber-400 space-y-2">
                      <div className="flex items-start gap-2.5">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <div className="text-xs leading-relaxed">
                          Tool removal requests require a <span className="font-bold">LinkedIn-verified account</span> with a work email matching <span className="font-mono font-bold">{toolDomain || 'company domain'}</span>.
                        </div>
                      </div>
                      <div className="pt-1">
                        <Link
                          href="/dashboard/profile"
                          target="_blank"
                          className="inline-flex items-center gap-1.5 text-xs font-bold underline hover:opacity-80"
                        >
                          Verify LinkedIn in Account Settings
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className={cn(
                      'p-3.5 rounded-xl border text-xs space-y-1.5',
                      domainMatches
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                        : 'bg-amber-500/10 border-amber-500/30 text-amber-500 dark:text-amber-400'
                    )}>
                      <div className="flex items-center gap-2 font-bold">
                        {domainMatches ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            Work Domain Matched ({userDomain})
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            Domain Note ({userDomain || 'personal email'} vs {toolDomain})
                          </>
                        )}
                      </div>
                      <p className="text-[11px] leading-relaxed opacity-90">
                        {domainMatches
                          ? 'Your LinkedIn-verified email matches this tool’s official domain.'
                          : 'Your email does not directly match the tool domain. Your request will be flagged for manual team verification.'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Subject */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground">
                  Subject {type === 'tool_removal' ? '(Optional)' : ''}
                </label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={
                    type === 'tool_removal'
                      ? `Removal Request: ${toolName || 'Tool'}`
                      : type === 'bug'
                      ? 'e.g. Broken link on review page'
                      : 'e.g. Add dark mode export'
                  }
                  className="rounded-xl bg-background/60 border-border/60 text-xs h-10"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground">
                  Description <span className="text-rose-500">*</span>
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    type === 'tool_removal'
                      ? 'Please explain why this tool should be removed or delisted (e.g. discontinued, copyright, official brand request)...'
                      : 'Please provide details, steps to reproduce, or your idea...'
                  }
                  rows={4}
                  required
                  className="rounded-xl bg-background/60 border-border/60 text-xs resize-none"
                />
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  className="rounded-xl text-xs font-bold h-10 px-4"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || (type === 'tool_removal' && !linkedinVerified)}
                  className="rounded-xl text-xs font-bold h-10 px-6 shadow-md"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
}
